"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { TokenRequest } from "../types/auth";
import { useAuthHook } from "@/hooks/useAuth";
import { useEnvironment } from "./EnvironmentContext";
import {
  getTokenForEnv,
  setTokenForEnv,
  clearTokenForEnv,
  markSessionReady,
  ENV_STORAGE_KEY,
  type Environment,
} from "../lib/auth-tokens";

interface User {
  id: string;
  email: string;
  name: string;
  companyName: string;
}

interface LoginResult {
  success: boolean;
  error?: string;
  tokenEnvironment?: Environment;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: TokenRequest) => Promise<LoginResult>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const userStorageKey = (env: Environment) => `auth_user_${env.toLowerCase()}`;

export function AuthProvider({ children }: { children: ReactNode }) {
  const { environment } = useEnvironment();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Reactive mirror of "do we have an access token right now" — getTokenForEnv
  // reads a plain Map mutated outside React, so it can't drive `enabled` on
  // the profile query directly: if nothing else happens to re-render this
  // component after the token is set (e.g. first login with no cached user,
  // where nothing below calls setState again), the query would stay disabled
  // forever. This state update is what guarantees a re-render happens.
  const [hasToken, setHasToken] = useState(false);
  const { profile } = useAuthHook(hasToken);

  // On mount and whenever the active environment changes:
  // 1. If the access token is already in memory (e.g. env switch mid-session), skip restore.
  // 2. Otherwise attempt a silent restore via the HttpOnly refresh cookie.
  // 3. Then hydrate user from localStorage (or wait for the profile query).
  useEffect(() => {
    setIsLoading(true);

    const init = async () => {
      try {
        if (!getTokenForEnv(environment)) {
          try {
            const res = await fetch("/api/auth/refresh", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ env: environment }),
            });
            if (res.ok) {
              const data = await res.json();
              if (data.access_token) {
                setTokenForEnv(environment, data.access_token);
              }
            }
          } catch {
            // Network error — continue without a token.
          }
        }

        if (!getTokenForEnv(environment)) {
          setUser(null);
          setHasToken(false);
          setIsLoading(false);
          return;
        }

        // Token exists (either already in memory or just restored above) —
        // make sure the reactive flag reflects it even on the "already in
        // memory" path, which never went through the refresh branch.
        setHasToken(true);

        try {
          const stored = localStorage.getItem(userStorageKey(environment));
          if (stored) {
            setUser(JSON.parse(stored) as User);
            setIsLoading(false);
          }
          // else: token present but no cached user — stay loading until profile resolves.
        } catch {
          setUser(null);
          setIsLoading(false);
        }
      } finally {
        // Unblock Axios requests regardless of outcome. The Promise resolves
        // only once — subsequent calls to markSessionReady() are no-ops.
        markSessionReady();
      }
    };

    init();
  }, [environment]);

  // Keep user in sync with live profile data from the API.
  useEffect(() => {
    if (profile.isFetching) return;
    if (profile.data) {
      const userData: User = {
        id: profile.data.id || "partner",
        email: profile.data.email || "",
        name: profile.data.name || "Partner",
        companyName: profile.data.company_name || "",
      };
      localStorage.setItem(userStorageKey(environment), JSON.stringify(userData));
      setUser(userData);
      setIsLoading(false);
    } else if (profile.isError) {
      setIsLoading(false);
    }
  }, [profile.data, profile.isFetching, profile.isError, environment]);

  const login = async (data: TokenRequest): Promise<LoginResult> => {
    setIsLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      const result = await res.json();

      if (!res.ok || !result.access_token) {
        setIsLoading(false);
        return {
          success: false,
          error: result.error || "Réponse sans jeton d'accès",
        };
      }

      const tokenEnvironment: Environment = result.tokenEnvironment;

      setTokenForEnv(tokenEnvironment, result.access_token);
      setHasToken(true);
      localStorage.setItem(ENV_STORAGE_KEY, tokenEnvironment);

      // Deliberately leave isLoading=true on success: the "keep user in
      // sync with profile" effect above resolves it once the profile fetch
      // actually confirms the session — same as the silent-restore path in
      // the init effect. If the bootstrap key needs rotation, that profile
      // request hangs forever (see the Axios 403 interceptor) and
      // RotationGateModal takes over instead; isLoading correctly never
      // resolves to "authenticated" in that case.
      return { success: true, tokenEnvironment };
    } catch (error) {
      setIsLoading(false);
      return {
        success: false,
        error: (error as Error).message || "Erreur lors de l'authentification",
      };
    }
  };

  const logout = useCallback(async () => {
    // Clear both environments, not just the current `environment` value —
    // it can be out of sync with which one the real session's cookie
    // belongs to (see setActiveEnvironment in EnvironmentContext.tsx), and
    // a surviving stale refresh-token cookie can silently resurrect a
    // session that was supposed to be logged out.
    clearTokenForEnv("SANDBOX");
    clearTokenForEnv("PRODUCTION");
    setHasToken(false);
    localStorage.removeItem(userStorageKey("SANDBOX"));
    localStorage.removeItem(userStorageKey("PRODUCTION"));
    setUser(null);

    await fetch("/api/auth/logout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    }).catch(() => {});

    window.location.replace("/login");
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated: !!user, isLoading, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
